import { useScrollAnimation, useAnimatedCounter } from '../hooks/useScrollAnimation';
import { LineChart, PieChart, Activity, Eye } from 'lucide-react';

export default function AnalyticsSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });
  const acosValue = useAnimatedCounter(18.5, isVisible, 2000);

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#05060B] py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(6,182,212,0.08)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div 
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-medium uppercase tracking-wider mb-6">
                Performance Analytics
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Insights that{' '}
                <span className="text-cyan-400 cyan-glow">drive action.</span>
              </h2>
              <p className="text-lg text-dark-400 mb-8">
                Our dashboards don't just show you what happened—they tell you what to do next. AI-powered recommendations based on your actual data.
              </p>

              <div className="space-y-4">
                {[
                  { icon: LineChart, title: 'Real-time Performance Tracking', desc: 'Live ACOS, TACOS, and conversion metrics' },
                  { icon: PieChart, title: 'Attribution Modeling', desc: 'Understand the true impact of every touchpoint' },
                  { icon: Activity, title: 'Anomaly Detection', desc: 'Get alerted when metrics deviate from baseline' },
                  { icon: Eye, title: 'Competitive Intelligence', desc: 'Track share of voice and competitor movements' },
                ].map((feature, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 rounded-xl bg-dark-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white mb-1">{feature.title}</h4>
                      <p className="text-sm text-dark-400">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div 
              className={`transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <div className="glass-card p-8">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-dark-900/50 border border-cyan-500/10">
                      <div className="text-xs text-dark-500 mb-1">ACOS (30d)</div>
                      <div className="text-2xl font-bold text-cyan-400">{acosValue}%</div>
                      <div className="text-xs text-green-400 mt-1">↓ 2.3% vs last month</div>
                    </div>
                    <div className="p-4 rounded-xl bg-dark-900/50 border border-cyan-500/10">
                      <div className="text-xs text-dark-500 mb-1">Ad Sales</div>
                      <div className="text-2xl font-bold text-white">$48.2K</div>
                      <div className="text-xs text-green-400 mt-1">↑ 12% vs last month</div>
                    </div>
                  </div>

                  <div className="h-48 bg-dark-900/50 rounded-xl border border-cyan-500/10 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-dark-400">Daily Spend vs Sales</span>
                      <div className="flex gap-2">
                        <span className="w-3 h-3 rounded-full bg-cyan-400" />
                        <span className="w-3 h-3 rounded-full bg-dark-600" />
                      </div>
                    </div>
                    <div className="flex items-end justify-between h-28 gap-2">
                      {[40, 65, 45, 80, 55, 90, 70, 85, 60, 75, 50, 95].map((h, i) => (
                        <div 
                          key={i} 
                          className="flex-1 bg-cyan-500/30 rounded-t"
                          style={{ height: `${h}%` }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 animate-pulse" />
                      <div>
                        <div className="text-sm font-medium text-white">AI Recommendation</div>
                        <div className="text-sm text-dark-400">
                          Increase bids on "kitchen organizer" keywords. Converting at 4.2x ROAS.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
